import React from 'react';
import { getReadTimeFromWords } from '../../../utils/read-time';
import LexicalArticleViewer from '../../../components/Lexical/LexicalArticleViewer';
import { type PerspectiveEntry } from './PersonaTabs';
import SteelmanTeaser from './SteelmanTeaser';
import ArgumentBrief from './ArgumentBrief';
import PullQuote from './PullQuote';
import VersusCard from './VersusCard';

interface PerspectiveArticleContentProps {
  perspective: PerspectiveEntry;
  publishedAt?: string;
  readingMode: 'brief' | 'full';
  onSwitchToFull: () => void;
}

const PerspectiveArticleContent: React.FC<PerspectiveArticleContentProps> = ({
  perspective,
  publishedAt,
  readingMode,
  onSwitchToFull
}) => {
  const initials = perspective.personaName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2);
  const dateStr = publishedAt
    ? new Date(publishedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';

  const lexicalContent = perspective.lexicalContent;
  if (!lexicalContent) {
    return (
      <div className="py-16 text-center text-slate-400">
        No article content available for this perspective.
      </div>
    );
  }

  const contentToRender = stripToggleNode(lexicalContent);

  const articleObj = {
    id: perspective.articleId,
    writingType: { name: 'Perspectives Analysis' },
    isShowSensitive: false
  };

  const showBrief = readingMode === 'brief' && perspective.argumentBrief;

  return (
    <div>
      {/* Steelman teaser */}
      {perspective.steelmanTeaser && (
        <SteelmanTeaser
          teaser={perspective.steelmanTeaser}
          personaColor={perspective.personaColor}
        />
      )}

      {/* Article title */}
      <h1
        className="text-[32px] font-extrabold text-slate-900 leading-tight mb-3"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        {perspective.articleTitle}
      </h1>
      {perspective.articleSubtitle && (
        <p
          className="text-lg text-slate-500 leading-relaxed mb-7"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        >
          {perspective.articleSubtitle}
        </p>
      )}

      {/* Author byline */}
      <div className="flex items-center gap-3 pb-6 border-b border-slate-200 mb-7">
        <div className="relative shrink-0">
          {perspective.personaAvatar ? (
            <img
              src={perspective.personaAvatar}
              alt={perspective.personaName}
              className="w-11 h-11 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold"
              style={{
                background: perspective.personaColor,
                fontFamily: "'JetBrains Mono', monospace"
              }}
            >
              {initials}
            </div>
          )}
          <div
            className="absolute font-extrabold rounded border-2 border-white"
            style={{
              bottom: '-2px',
              right: '-4px',
              background: '#0f172a',
              color: '#a5f3fc',
              fontSize: '7px',
              padding: '2px 4px',
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.05em'
            }}
          >
            AI
          </div>
        </div>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">
            {perspective.personaName}
            <span
              className="ml-2 text-[11px] px-2 py-0.5 rounded font-bold"
              style={{
                background: `${perspective.personaColor}15`,
                color: perspective.personaColor,
                fontFamily: "'JetBrains Mono', monospace"
              }}
            >
              {perspective.personaIdeology}
            </span>
          </div>
          <div className="text-[13px] text-slate-400">
            <span
              className="font-semibold text-slate-500"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}
            >
              AI Persona
            </span>
            {dateStr && <span> · {dateStr}</span>}
            {perspective.wordCount ? (
              <span> · {getReadTimeFromWords(perspective.wordCount)}</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Content — either argument brief or full Lexical */}
      {showBrief ? (
        <ArgumentBrief
          thesis={perspective.argumentBrief!.thesis}
          keyArguments={perspective.argumentBrief!.keyArguments}
          personaColor={perspective.personaColor}
          onReadFull={onSwitchToFull}
        />
      ) : (
        <LexicalArticleViewerWithEngagement
          perspective={perspective}
          contentToRender={contentToRender}
          articleObj={articleObj}
        />
      )}
    </div>
  );
};

interface LexicalWithEngagementProps {
  perspective: PerspectiveEntry;
  contentToRender: any;
  articleObj: any;
}

/**
 * Wraps LexicalArticleViewer and injects pull quotes / versus cards
 * at the paragraph positions specified by the engagement data.
 *
 * Because Lexical renders its own nodes, we inject engagement elements
 * by wrapping the viewer and rendering the inline items below it.
 * A more integrated approach would be custom Lexical nodes, but for now
 * appending them after the viewer keeps the change minimal and backward-compatible.
 */
const LexicalArticleViewerWithEngagement: React.FC<LexicalWithEngagementProps> = ({
  perspective,
  contentToRender,
  articleObj
}) => {
  const pullQuotes = perspective.pullQuotes || [];
  const versusCards = perspective.versusCards || [];

  const hasInlineEngagement = pullQuotes.length > 0 || versusCards.length > 0;

  if (!hasInlineEngagement) {
    return (
      <LexicalArticleViewer
        key={perspective.articleId}
        showSensitiveContentCard={false}
        content={contentToRender}
        articleId={perspective.articleId || ''}
        article={articleObj}
      />
    );
  }

  // Inject engagement elements by interleaving them with paragraph nodes
  // Since Lexical manages its own DOM, we render the viewer first
  // and add engagement components before/after it keyed to their positions
  const sortedEngagement = [
    ...pullQuotes.map((pq) => ({
      type: 'pullQuote' as const,
      afterParagraph: pq.afterParagraph,
      data: pq
    })),
    ...versusCards.map((vc) => ({
      type: 'versusCard' as const,
      afterParagraph: vc.afterParagraph,
      data: vc
    }))
  ].sort((a, b) => a.afterParagraph - b.afterParagraph);

  // Split engagement into early (after paragraph 0-1) and late (after paragraph 2+)
  const earlyItems = sortedEngagement.filter((e) => e.afterParagraph <= 1);
  const lateItems = sortedEngagement.filter((e) => e.afterParagraph > 1);

  return (
    <div>
      {/* Early engagement items render before the Lexical viewer */}
      {earlyItems.length > 0 && (
        <div className="engagement-early">
          {earlyItems.map((item, i) => (
            <React.Fragment key={`early-${i}`}>
              {item.type === 'pullQuote' && (
                <PullQuote text={(item.data as any).text} personaColor={perspective.personaColor} />
              )}
              {item.type === 'versusCard' && (
                <VersusCard
                  topic={(item.data as any).topic}
                  thisView={(item.data as any).thisView}
                  otherView={(item.data as any).otherView}
                  personaColor={perspective.personaColor}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <LexicalArticleViewer
        key={perspective.articleId}
        showSensitiveContentCard={false}
        content={contentToRender}
        articleId={perspective.articleId || ''}
        article={articleObj}
      />

      {/* Late engagement items render after the main content */}
      {lateItems.length > 0 && (
        <div className="engagement-late">
          {lateItems.map((item, i) => (
            <React.Fragment key={`late-${i}`}>
              {item.type === 'pullQuote' && (
                <PullQuote text={(item.data as any).text} personaColor={perspective.personaColor} />
              )}
              {item.type === 'versusCard' && (
                <VersusCard
                  topic={(item.data as any).topic}
                  thisView={(item.data as any).thisView}
                  otherView={(item.data as any).otherView}
                  personaColor={perspective.personaColor}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

function stripToggleNode(content: any): any {
  if (!content) return content;
  try {
    const state =
      typeof content === 'string' ? JSON.parse(content) : JSON.parse(JSON.stringify(content));
    const root = state.root || state.editorState?.root;
    if (root?.children) {
      root.children = root.children.filter((child: any) => child.type !== 'perspective-toggle');
    }
    return state;
  } catch {
    return content;
  }
}

export default PerspectiveArticleContent;
